<template>
    <div>
        <app-popup ref="appPopup"></app-popup>
        <div class="row" v-if="showCompanyData">
            <div class="col-12 form-group" v-if="showSearchBox && allowAnonymous">
                <span class="float-right">
                    <company-search :showId="showId"
                                        @onCompanySelected="onCompanySelected">
                    </company-search>
                </span>
            </div>
            <div class="form-group col-lg-4">
                <label class="font-weight-bold required" :for="'name' + _uid">{{ $t('company.name') }}</label>
                <input type="text" class="form-control"
                       v-model="company.name"
                       v-validate="isDifferentName && checkCompanyNameUniqueness ? `required|uniqueCompanyName` : 'required'"
                       :data-vv-as="$t('company.name')"
                       :name="'name' + _uid"
                       :id="'name' + _uid">
                <span class="text-danger" v-if="errors.has('name' + _uid)">{{ errors.first('name' + _uid) }}</span>
            </div>
            <div class="form-group col-lg-4">
                <label  class="font-weight-bold" :class="vatNumberRequired ? 'required': ''" :for="'vatNumber' + _uid">
                    {{ $t('company.vatNumber') }}
                </label>
                <i class="fas fa-search pointer text-primary float-right"
                   data-toggle="tooltip"
                   :title="vatCheckTitle"
                   @click="checkVatNumber">
                </i>
                <input type="text" class="form-control"
                       v-model="company.vatNumber"
                       v-validate="vatNumberValidation"
                       :data-vv-as="$t('company.vatNumber')"
                       :name="'vatNumber' + _uid"
                       :id="'vatNumber' + _uid">
                <span class="text-danger"
                      v-if="errors.has('vatNumber' + _uid)">{{ errors.first('vatNumber' + _uid) }}</span>
            </div>
            <div class="form-group col-lg-4">
                <label class="font-weight-bold" :for="'bulstatUic' + _uid">{{ $t('company.bulstatUic') }}</label>
                <input type="text" class="form-control" v-model="company.bulstatUic" :id="'bulstatUic' + _uid">
            </div>
            <div class="form-group col-lg-4">
                <label class="font-weight-bold required" :for="'email' + _uid">{{ $t('email') }}</label>
                <input type="text" class="form-control"
                       v-model="company.email"
                       v-validate="'required'"
                       :data-vv-as="$t('email')"
                       :name="'email' + _uid"
                       :id="'email' + _uid">
                <span class="text-danger" v-if="errors.has('email' + _uid)">{{ errors.first('email' + _uid) }}</span>
            </div>
            <div class="form-group col-lg-4">
                <label class="font-weight-bold" :for="'fax' + _uid">{{ $t('company.fax') }}</label>
                <input type="text" class="form-control" v-model="company.fax" :id="'fax' + _uid">
            </div>
            <div class="form-group col-lg-4">
                <label class="font-weight-bold required" :for="'phone' + _uid">{{ $t('company.phone') }}</label>
                <input type="text" class="form-control"
                       v-model="company.phone"
                       v-validate="'required'"
                       :data-vv-as="$t('company.phone')"
                       :name="'phone' + _uid"
                       :id="'phone' + _uid">
                <span class="text-danger" v-if="errors.has('phone' + _uid)">{{ errors.first('phone' + _uid) }}</span>
            </div>
            <div class="form-group col-lg-4">
                <label class="font-weight-bold" :for="'url' + _uid">{{ $t('company.url') }}</label>
                <input type="text" class="form-control" v-model="company.url" :id="'url' + _uid">
            </div>
            <div class="form-group col-lg-12">
                <label class="font-weight-bold" :for="'description' + _uid">{{ $t('company.description') }}</label>
                <textarea rows="3" class="form-control" v-model="company.description" :id="'description' + _uid"
                          :maxlength="maxLength"></textarea>
                <p>{{$t('remainingSymbols')}} {{charactersRemaining}}</p>
            </div>
        </div>
        <br>
        <contact-person v-if="showCompanyManager && company.manager"
                        :person="company.manager"
                        :title="$t('company.manager')"
                        :requiredIdentification="false">
        </contact-person>
        <div class="row" v-if="showCompanyManager">
            <button type="button"
                    class="btn btn-primary"
                    v-if="showCompanyManagerButton && company.manager"
                    @click="cloneManagerPerson">
                <i class="fas fa-copy"></i> {{ $t('person.cloneManager') }}
            </button>
            <button type="button"
                    class="btn btn-primary"
                    v-if="showCompanyManagerResetButton && company.manager"
                    @click="resetManagerPerson">
                <i class="fa fa-edit"></i> {{ $t('person.resetManager') }}
            </button>
            <button type="button"
                    class="btn btn-danger"
                    v-if="removableManager && company.manager"
                    @click="removeManagerPerson">
                <i class="fas fa-times"></i> {{ $t('person.removeManager') }}
            </button>
            <button type="button"
                    class="btn btn-success"
                    v-if="removableManager && !company.manager"
                    @click="addManagerPerson">
                <i class="fas fa-plus"></i> {{ $t('person.addManager') }}
            </button>
        </div>
        <br>
        <contact-person v-if="showCompanyContact && company.contact"
                        :person="company.contact"
                        :title="$t('company.contact')"
                        :requiredIdentification="false">
        </contact-person>
        <div class="row" v-if="showCompanyContact">
            <button type="button"
                    class="btn btn-primary"
                    v-if="company.contact"
                    @click="cloneContactPerson">
                <i class="fas fa-copy"></i> {{ $t('person.cloneContact') }}
            </button>
            <button type="button"
                    class="btn btn-primary"
                    v-if="showCompanyContactResetButton && company.contact"
                    @click="resetContactPerson">
                <i class="fa fa-edit"></i> {{ $t('person.resetContact') }}
            </button>
            <button type="button"
                    class="btn btn-danger"
                    v-if="removableContact && company.contact"
                    @click="removeContactPerson">
                <i class="fas fa-times"></i> {{ $t('person.removeContact') }}
            </button>
            <button type="button"
                    class="btn btn-success"
                    v-if="removableContact && !company.contact"
                    @click="addContactPerson">
                <i class="fas fa-plus"></i> {{ $t('person.addContact') }}
            </button>
        </div>
        <br>
        <app-address ref="registeredAddress"
                     :address="company.registeredAddress"
                     :showAreaOfAppeal="showAreaOfAppeal"
                     :title="$t('company.registeredAddress')" v-if="showRegisteredAddress"></app-address>
        <div class="row" v-if="showRegisteredAddress">
            <button type="button"
                    class="btn btn-primary"
                    @click="cloneRegisteredAddress">
                <i class="fas fa-copy"></i> {{ $t('address.cloneRegistrationAddress') }}
            </button>
        </div>
        <br>
        <app-address ref="contactAddress" :address="company.contactAddress" :title="$t('company.contactAddress')"
                     v-if="showContactAddress"></app-address>
        <div class="row" v-if="showContactAddress">
            <button type="button"
                    class="btn btn-primary"
                    @click="cloneContactAddress">
                <i class="fas fa-copy"></i> {{ $t('address.cloneContactAddress') }}
            </button>
        </div>
        <br>
        <bank-account :bankAccount="company.bankAccount" :required="false" :title="$t('company.bankAccount')"
                      v-if="showBankAccount"></bank-account>
    </div>
</template>

<script>
    import AddressComponent from '@/components/address/Address';
    import BankAccountComponent from '@/components/bankAccount/BankAccount';
    import MsgPopup from "@/components/common/MsgPopup.vue";
    import { Company, Person, Address, BankAccount, AreaOfAppeal } from '@/models/models';
    import CompanySearch from '@/components/common/CompanySearch';
    import { mapGetters } from 'vuex';
    import ContactPerson from "@/components/person/ContactPerson";

    export default {
        inject: ['$validator'],
        props: {
            showContactAddress: {
                Boolean,
                default: true
            },
            showBankAccount: {
                Boolean,
                default: true
            },
            showRegisteredAddress: {
                Boolean,
                default: true
            },
            showCompanyContact: {
                Boolean,
                default: true
            },
            showCompanyManager: {
                Boolean,
                default: true
            },
            showCompanyManagerButton: {
                Boolean,
                default: true
            },
            showCompanyData: {
                Boolean,
                default: true
            },
            showAreaOfAppeal: {
                Boolean,
                default: false
            },
            showCompanyManagerResetButton: {
                Boolean,
                default: true
            },
            showCompanyContactResetButton: {
                Boolean,
                default: true
            },
            maxLength: {
                Number,
                default: 10000
            },
            showSearchBox: {
                Boolean,
                default: true
            },
            allowAnonymous: {
                type: Boolean,
                default: true
            },
            showId: {
                type: Boolean,
                default: false
            },
            company: Company,
            checkVatUniqueness: {
                type: Boolean,
                default: true
            },
            checkCompanyNameUniqueness: {
                type: Boolean,
                default: true
            },
            vatNumberRequired: {
                type: Boolean,
                default: true
            },
            removableManager: {
                type: Boolean,
                default: false
            },
            removableContact: {
                type: Boolean,
                default: false
            },
        },
        components: {
            ContactPerson,
            'app-address': AddressComponent,
            'bank-account': BankAccountComponent,
            'app-popup': MsgPopup,
            'company-search': CompanySearch,
        },
        data() {
            return {
                vatCheckTitle: this.$t('vatCheckTitle'),
                isDifferentVATNumber: false,
                isDifferentName: false,
                oldVATNumber: '',
                oldCompanyName: '',
                companySearchBoxId: '',
                vatCheckResult: '',
            };
        },
        created() {
            if (this.checkCompanyNameUniqueness) {
                this.$validator.extend('uniqueCompanyName', {
                    getMessage: () => `${this.$t('companyNameUnique')}`,
                    validate: value => this.$http.get(`/api/Services/isCompanyNameUnique/${this.company.id}/${value}`)
                        .then(response => {
                            if (response) {
                                return response.data;
                            }
                        })
                });
            }

            if (this.checkVatUniqueness) {
                this.$validator.extend('uniqueVatNumber', {
                    getMessage: () => `${this.$t('vatNumberUnique', { name: this.vatCheckResult ? (this.isEnVersion && this.vatCheckResult.companyNameEn ? this.vatCheckResult.companyNameEn : this.vatCheckResult.companyName) : '' })}`,
                    validate: value => this.$http.get(`/api/Services/isVATNumberUnique/${this.company.id}/${value}`)
                        .then(response => {
                            this.vatCheckResult = '';
                            if (response && response.data) {
                                this.vatCheckResult = response.data;
                                return response.data.isUnique;
                            }
                        })
                });
            }
        },
        mounted: function () {
            var container = $(this.$el);
            container.find('[data-toggle="tooltip"]').tooltip({ container });
            this.oldVATNumber = this.company.vatNumber;
            this.oldCompanyName = this.company.name;

            if (!this.isNew) {
                this.companySearchBoxId = this.company.id;
            }
        },
        watch: {
            'company.vatNumber'(newVal, oldVal) {
                if (newVal === this.oldVATNumber) {
                    this.isDifferentVATNumber = false;
                } else {
                    this.isDifferentVATNumber = true;
                }
            },
            'company.name'(newVal, oldVal) {
                if (newVal === this.oldCompanyName) {
                    this.isDifferentName = false;
                } else {
                    this.isDifferentName = true;
                }
            },
            companySearchBoxId(newVal, oldVal) {
                this.getCompanyData(newVal);
            }
        },
        computed: {
            ...mapGetters(['isEnVersion']),
            charactersRemaining: function () {
                return this.maxLength - this.company.description.length;
            },
            vatNumberValidation() {
                let rules = ['max:15'];

                if (this.vatNumberRequired) {
                    rules.unshift('required');
                }

                if (this.isDifferentVATNumber && this.checkVatUniqueness) {
                    rules.push('uniqueVatNumber');
                }

                return rules.join('|');
            }
        },
        methods: {
            checkVatNumber() {
                this.$http.get('/api/Services/vatNumberCheck/' + this.company.vatNumber)
                    .then(response => {
                        this.$refs.appPopup.show(response.data.isValid === true ? 'success' : "danger",
                            this.$t('vatCheckTitle'),
                            '<div class="text-center">' + response.data.msg + '</div>', true);
                    }).catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            isCompanyNameUnique2(name) {
                var result
                this.$http.get(`/api/Services/isCompanyNameUnique/${this.company.id}/${name}`)
                    .then(response => {
                        if (response) {
                            result = response.data;
                        }
                        return result;
                    })
            },
            resetManagerPerson() {
                this.company.manager = new Person();
            },
            resetContactPerson() {
                this.company.contact = new Person();
            },
            removeManagerPerson() {
                this.company.manager = null;
            },
            addManagerPerson() {
                this.company.manager = new Person();
            },
            removeContactPerson() {
                this.company.contact = null;
            },
            addContactPerson() {
                this.company.contact = new Person();
            },
            cloneManagerPerson: function () {
                if (!this.company.manager) {
                    return;
                }
                if (!this.company.contact) {
                    this.company.contact = new Person();
                }

                this.company.contact.firstName = this.company.manager.firstName;
                this.company.contact.middleName = this.company.manager.middleName;
                this.company.contact.lastName = this.company.manager.lastName;
                this.company.contact.genderTypeId = this.company.manager.genderTypeId;
                this.company.contact.phone = this.company.manager.phone;
                this.company.contact.mobilePhone = this.company.manager.mobilePhone;
                this.company.contact.email = this.company.manager.email;
                this.company.contact.position = this.company.manager.position;
                this.company.contact.description = this.company.manager.description;
                this.company.contact.personIdentification.identificationTypeId = this.company.manager.personIdentification.identificationTypeId;
                this.company.contact.personIdentification.number = this.company.manager.personIdentification.number;
                this.company.contact.institutionRegisterId = this.company.manager.institutionRegisterId;
            },
            cloneContactPerson: function () {
                if (!this.company.contact) {
                    return;
                }
                if (!this.company.manager) {
                    this.company.manager = new Person();
                }

                this.company.manager.firstName = this.company.contact.firstName;
                this.company.manager.middleName = this.company.contact.middleName;
                this.company.manager.lastName = this.company.contact.lastName;
                this.company.manager.genderTypeId = this.company.contact.genderTypeId;
                this.company.manager.phone = this.company.contact.phone;
                this.company.manager.mobilePhone = this.company.contact.mobilePhone;
                this.company.manager.email = this.company.contact.email;
                this.company.manager.position = this.company.contact.position;
                this.company.manager.description = this.company.contact.description;
                this.company.manager.personIdentification.identificationTypeId = this.company.contact.personIdentification.identificationTypeId;
                this.company.manager.personIdentification.number = this.company.contact.personIdentification.number;
                this.company.manager.institutionRegisterId = this.company.contact.institutionRegisterId;
            },
            cloneRegisteredAddress: function () {
                if (!this.company.registeredAddress) {
                    return;
                }
                if (!this.company.contactAddress) {
                    this.company.contactAddress = new Address();
                }

                this.company.contactAddress.countryId = this.company.registeredAddress.countryId;
                this.company.contactAddress.postCode = this.company.registeredAddress.postCode;
                this.company.contactAddress.street = this.company.registeredAddress.street;
                this.company.contactAddress.num = this.company.registeredAddress.num;
                this.company.contactAddress.lat = this.company.registeredAddress.lat;
                this.company.contactAddress.long = this.company.registeredAddress.long;
                this.company.contactAddress.settlementId = this.company.registeredAddress.settlementId;
                this.company.contactAddress.settlement = this.company.registeredAddress.settlement;
                this.company.contactAddress.cityName = this.company.registeredAddress.cityName;
                this.company.contactAddress.areaOfAppeal = new AreaOfAppeal(this.company.registeredAddress.areaOfAppeal)
            },
            cloneContactAddress: function () {
                if (!this.company.contactAddress) {
                    return;
                }
                if (!this.company.registeredAddress) {
                    this.company.registeredAddress = new Address();
                }

                this.company.registeredAddress.countryId = this.company.contactAddress.countryId;
                this.company.registeredAddress.postCode = this.company.contactAddress.postCode;
                this.company.registeredAddress.street = this.company.contactAddress.street;
                this.company.registeredAddress.num = this.company.contactAddress.num;
                this.company.registeredAddress.lat = this.company.contactAddress.lat;
                this.company.registeredAddress.long = this.company.contactAddress.long;
                this.company.registeredAddress.settlementId = this.company.contactAddress.settlementId;
                this.company.registeredAddress.settlement = this.company.contactAddress.settlement;
                this.company.registeredAddress.cityName = this.company.contactAddress.cityName;
                this.company.registeredAddress.areaOfAppeal = new AreaOfAppeal(this.company.contactAddress.areaOfAppeal)
            },
            onCompanySelected(companySearchModel) {
                this.companySearchBoxId = companySearchModel.id;
            },
            getCompanyData(id) {
                // TODO: Loading
                this.$http
                    .get(`/api/company/${id}`)
                    .then(result => {
                        this.populateCompany(result.data);
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t(
                                'request.response.status.' +
                                this.$helpers.errorStatusCode(error)
                            ),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            populateCompany(p) {
                this.company.id = p.id;
                this.company.name = p.name;
                this.company.vatNumber = p.vatNumber;
                this.company.bulstatUic = p.bulstatUic;
                this.company.phone = p.phone || '';
                this.company.fax = p.fax || '';
                this.company.url = p.url || '';
                this.company.email = p.email || '';
                this.company.description = p.description || '';
                this.company.manager = p.manager ? new Person(p.manager) : !this.removableManager ? new Person({}) : null;
                this.company.contact = p.contact ? new Person(p.contact) : !this.removableContact ? new Person({}) : null;
                this.company.registeredAddress = new Address(p.registeredAddress || {});
                this.company.contactAddress = new Address(p.contactAddress || {});
                this.company.bankAccount = new BankAccount(p.bankAccount || {});
            }
        }
    }
</script>

<style scoped>
</style>
